import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import styles from './Banners.module.css';

interface Banner {
  id: number;
  imageUrl: string;
  mobileImageUrl: string | null;
  link: string | null;
  position: number;
  active: boolean;
  createdAt: string;
}

const EMPTY_FORM = { imageUrl: '', mobileImageUrl: '', link: '', position: 1, active: true };

// Convertit l'image en WebP côté navigateur avant l'envoi (réduit fortement la taille,
// évite de dépasser la limite de taille de requête du serveur). Les GIF sont laissés
// tels quels pour ne pas casser leur animation.
async function toWebp(file: File, quality = 0.85): Promise<File> {
  if (file.type === 'image/gif' || !('createImageBitmap' in window)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0);
    const blob: Blob | null = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', quality));
    if (!blob) return file;
    const newName = file.name.replace(/\.[^.]+$/, '') + '.webp';
    return new File([blob], newName, { type: 'image/webp' });
  } catch {
    return file;
  }
}

export const Banners: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const mobileFileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => { loadBanners(); }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get<Banner[]>('banners/admin');
      const sorted = (res.data ?? []).sort((a, b) => a.position - b.position);
      setBanners(sorted);
    } catch (err: any) {
      setError('Erreur chargement : ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    const nextPos = banners.length > 0 ? Math.max(...banners.map(b => b.position)) + 1 : 1;
    setForm({ ...EMPTY_FORM, position: nextPos });
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (b: Banner) => {
    setForm({ imageUrl: b.imageUrl, mobileImageUrl: b.mobileImageUrl ?? '', link: b.link ?? '', position: b.position, active: b.active });
    setEditingId(b.id);
    setShowForm(true);
  };

  const cancelForm = () => { setShowForm(false); setEditingId(null); };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', await toWebp(file));
      const res = await api.post<{ url: string; fileName: string }>('banners/upload-image', formData, {
        headers: { 'Content-Type': undefined },
      });
      setForm(f => ({ ...f, imageUrl: res.data.url }));
    } catch (err: any) {
      alert('Erreur upload : ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleMobileFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingMobile(true);
      const formData = new FormData();
      formData.append('file', await toWebp(file));
      const res = await api.post<{ url: string; fileName: string }>('banners/upload-image', formData, {
        headers: { 'Content-Type': undefined },
      });
      setForm(f => ({ ...f, mobileImageUrl: res.data.url }));
    } catch (err: any) {
      alert('Erreur upload : ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingMobile(false);
      if (mobileFileInputRef.current) mobileFileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.imageUrl.trim()) { alert('URL de l\'image requise.'); return; }
    try {
      setSaving(true);
      const payload = { ...form, link: form.link.trim() || null, mobileImageUrl: form.mobileImageUrl.trim() || null };
      if (editingId !== null) {
        await api.put(`banners/${editingId}`, payload);
      } else {
        await api.post('banners', payload);
      }
      await loadBanners();
      cancelForm();
    } catch (err: any) {
      alert('Erreur : ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Supprimer cette bannière ?')) return;
    try {
      await api.delete(`banners/${id}`);
      setBanners(prev => prev.filter(b => b.id !== id));
    } catch (err: any) {
      alert('Erreur : ' + (err.response?.data?.message || err.message));
    }
  };

  const handleToggleActive = async (b: Banner) => {
    try {
      await api.put(`banners/${b.id}`, { ...b, link: b.link ?? null, active: !b.active });
      setBanners(prev => prev.map(x => x.id === b.id ? { ...x, active: !x.active } : x));
    } catch (err: any) {
      alert('Erreur : ' + (err.response?.data?.message || err.message));
    }
  };

  const handleMove = async (index: number, dir: -1 | 1) => {
    const swapIndex = index + dir;
    if (swapIndex < 0 || swapIndex >= banners.length) return;
    const a = banners[index];
    const b = banners[swapIndex];
    try {
      await Promise.all([
        api.put(`banners/${a.id}`, { ...a, link: a.link ?? null, position: b.position }),
        api.put(`banners/${b.id}`, { ...b, link: b.link ?? null, position: a.position }),
      ]);
      const updated = [...banners];
      updated[index] = { ...a, position: b.position };
      updated[swapIndex] = { ...b, position: a.position };
      setBanners(updated.sort((x, y) => x.position - y.position));
    } catch (err: any) {
      alert('Erreur réordonnancement : ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <h1 className={styles.title}>Bannières carousel</h1>
          <button className={styles.btnAdd} onClick={openAdd}>+ Ajouter une bannière</button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* Formulaire ajout / modification */}
        {showForm && (
          <div className={styles.formCard}>
            <h2 className={styles.formTitle}>{editingId !== null ? 'Modifier la bannière' : 'Nouvelle bannière'}</h2>
            <div className={styles.formGrid}>
              <div className={styles.fieldFull}>
                <label className={styles.label}>Image (desktop) *</label>
                <div className={styles.imageInputRow}>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="https://eliquidesud.com/Images/banniere_..."
                    value={form.imageUrl}
                    onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                  />
                  <button
                    type="button"
                    className={styles.btnUpload}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? '⏳ Upload...' : '📁 Uploader'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                </div>
                <div className={styles.hint}>Sera converti en WebP et déposé sur le serveur automatiquement.</div>
                {form.imageUrl && (
                  <img src={form.imageUrl} alt="aperçu" className={styles.preview} onError={e => (e.currentTarget.style.display = 'none')} />
                )}
              </div>
              <div className={styles.fieldFull}>
                <label className={styles.label}>Image mobile (optionnel)</label>
                <div className={styles.imageInputRow}>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="https://eliquidesud.com/Images/banniere_..."
                    value={form.mobileImageUrl}
                    onChange={e => setForm(f => ({ ...f, mobileImageUrl: e.target.value }))}
                  />
                  <button
                    type="button"
                    className={styles.btnUpload}
                    onClick={() => mobileFileInputRef.current?.click()}
                    disabled={uploadingMobile}
                  >
                    {uploadingMobile ? '⏳ Upload...' : '📁 Uploader'}
                  </button>
                  <input
                    ref={mobileFileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleMobileFileUpload}
                  />
                </div>
                <div className={styles.hint}>Si non renseignée, l'image principale sera utilisée sur mobile.</div>
                {form.mobileImageUrl && (
                  <img src={form.mobileImageUrl} alt="aperçu mobile" className={styles.preview} onError={e => (e.currentTarget.style.display = 'none')} />
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Lien (optionnel)</label>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="/produits ou https://..."
                  value={form.link}
                  onChange={e => setForm(f => ({ ...f, link: e.target.value }))}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Position</label>
                <input
                  type="number"
                  className={styles.input}
                  min={1}
                  value={form.position}
                  onChange={e => setForm(f => ({ ...f, position: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className={styles.fieldToggle}>
                <label className={styles.label}>Active</label>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            </div>
            <div className={styles.formActions}>
              <button className={styles.btnSave} onClick={handleSave} disabled={saving}>
                {saving ? 'Enregistrement...' : editingId !== null ? '✓ Enregistrer' : '+ Créer'}
              </button>
              <button className={styles.btnCancel} onClick={cancelForm}>Annuler</button>
            </div>
          </div>
        )}

        {/* Liste des bannières */}
        {!loading && banners.length === 0 ? (
          <div className={styles.noData}>Aucune bannière. Cliquez sur "Ajouter" pour commencer.</div>
        ) : (
          <div className={styles.list}>
            {banners.map((b, i) => (
              <div key={b.id} className={`${styles.bannerRow} ${!b.active ? styles.bannerInactive : ''}`}>
                <div className={styles.orderBtns}>
                  <button className={styles.btnOrder} onClick={() => handleMove(i, -1)} disabled={i === 0}>▲</button>
                  <span className={styles.posLabel}>{b.position}</span>
                  <button className={styles.btnOrder} onClick={() => handleMove(i, 1)} disabled={i === banners.length - 1}>▼</button>
                </div>

                <img
                  src={b.imageUrl}
                  alt={`bannière ${b.position}`}
                  className={styles.thumbnail}
                  onError={e => { e.currentTarget.src = ''; e.currentTarget.style.display = 'none'; }}
                />

                <div className={styles.bannerInfo}>
                  <div className={styles.bannerUrl} title={b.imageUrl}>{b.imageUrl}</div>
                  {b.link && <div className={styles.bannerLink}>🔗 {b.link}</div>}
                </div>

                <label className={styles.toggle} title={b.active ? 'Désactiver' : 'Activer'}>
                  <input type="checkbox" checked={b.active} onChange={() => handleToggleActive(b)} />
                  <span className={styles.toggleSlider} />
                </label>

                <div className={styles.rowActions}>
                  <button className={styles.btnEdit} onClick={() => openEdit(b)}>✏️ Modifier</button>
                  <button className={styles.btnDelete} onClick={() => handleDelete(b.id)}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};
